import React, { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { CalendarDays, Clock, Video, Coffee, Phone, MapPin } from "lucide-react";
import { toast, Toaster } from "sonner";
import { InitialsBadge } from "../../components/DashboardLayout";

const FORMAT_LABELS = {
    visio: { icon: Video, label: "Visio" },
    cafe: { icon: Coffee, label: "Café" },
    telephone: { icon: Phone, label: "Téléphone" },
    presentiel: { icon: MapPin, label: "Présentiel" },
};
const TYPE_LABELS = {
    parcoursup: "Parcoursup",
    specialites: "Spécialités",
    bilan: "Bilan",
    reorientation: "Réorientation",
    decouverte: "Découverte",
    suivi: "Suivi",
};

export default function MesRdv() {
    const [appts, setAppts] = useState([]);

    useEffect(() => {
        api.get("/me/appointments").then((r) => setAppts(r.data)).catch(() => toast.error("Erreur de chargement"));
    }, []);

    const { upcoming, past } = useMemo(() => {
        const now = new Date();
        const u = [];
        const p = [];
        appts.forEach((a) => {
            if (new Date(a.starts_at) >= now) u.push(a);
            else p.push(a);
        });
        p.reverse();
        return { upcoming: u, past: p };
    }, [appts]);

    return (
        <div data-testid="client-page-rdv" className="space-y-8 max-w-4xl">
            <Toaster position="top-center" theme="dark" richColors />
            <div>
                <span className="text-xs tracking-[0.3em] text-ice uppercase">— Mes rendez-vous</span>
                <h1 className="font-serif text-4xl mt-2">Rendez-vous</h1>
            </div>

            <section>
                <h3 className="font-serif text-2xl mb-4">À venir</h3>
                {upcoming.length === 0 ? (
                    <div className="text-sm text-white/50 bg-ink-600 border border-white/10 rounded-xl p-6">
                        Aucun rendez-vous à venir. Votre conseillère en planifiera un prochainement.
                    </div>
                ) : (
                    <div className="space-y-3">
                        {upcoming.map((a) => (
                            <AppointmentCard key={a.id} appt={a} upcoming />
                        ))}
                    </div>
                )}
            </section>

            {past.length > 0 && (
                <section>
                    <h3 className="font-serif text-2xl mb-4">Historique</h3>
                    <div className="space-y-3 opacity-75">
                        {past.map((a) => (
                            <AppointmentCard key={a.id} appt={a} />
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
}

function AppointmentCard({ appt, upcoming }) {
    const fmt = FORMAT_LABELS[appt.format] || { icon: Video, label: appt.format };
    const FIcon = fmt.icon;
    const d = new Date(appt.starts_at);
    return (
        <div
            data-testid={`rdv-${appt.id}`}
            className={`bg-ink-600 rounded-xl border p-5 flex flex-wrap items-center gap-5 ${
                upcoming ? "border-ice/30" : "border-white/10"
            }`}
        >
            <div className="flex items-center gap-4 flex-1 min-w-[200px]">
                <div className="w-14 h-14 rounded-md bg-ink-800 border border-ice/20 flex flex-col items-center justify-center shrink-0">
                    <div className="text-[10px] uppercase tracking-wider text-ice">
                        {d.toLocaleString("fr-FR", { month: "short" })}
                    </div>
                    <div className="font-serif text-xl leading-none mt-0.5">{d.getDate()}</div>
                </div>
                <div>
                    <div className="font-medium">{TYPE_LABELS[appt.type] || appt.type}</div>
                    <div className="text-xs text-white/60 mt-0.5 flex items-center gap-2">
                        <Clock size={12} /> {d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                        <span className="text-white/30">·</span>
                        <span>{appt.duration_min} min</span>
                    </div>
                </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-white/75">
                <FIcon size={15} className="text-ice" /> {fmt.label}
            </div>
            <div className="flex items-center gap-2">
                <span className="text-xs text-white/55">Avec</span>
                <InitialsBadge value={appt.consultant} size="sm" tone="ice" />
            </div>
        </div>
    );
}
